import TableContainer from 'Components/Common/TableContainerReactTable';
import React, { useRef, useState } from 'react';
import { Card, CardBody, CardHeader, Col, Input, Spinner } from 'reactstrap';
// import FocalPersonDetailsModal from './FocalPersonDetailsModal';
import { debounce } from 'lodash';
import { useQuery } from '@tanstack/react-query';
import { focalPersonListing } from 'endpoints/builder/builderDetails.action';
import { useSelector } from 'react-redux';
import { getTruncatedId } from 'lib/utils/formatters';

const FocalPersonTable = () => {
    const builderId = useSelector((state: any) => state.Builder.selectedBuilderId);

    const [searchInput, setSearchInput] = useState('');
    const [searchTerm, setSearchTerm] = useState('');
    const [currentPage, setCurrentPage] = useState(1);
    const [limit] = useState(10);
    const [copiedId, setCopiedId] = useState<string | null>(null);

    // Debounced search
    const debouncedSearch = useRef(
        debounce((value: string) => {
            setSearchTerm(value);
            setCurrentPage(1);
        }, 500)
    ).current;

    const handleSearch = (value: string) => {
        debouncedSearch(value);
    };

    const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value;
        setSearchInput(value);
        handleSearch(value);
    };

    const clearSearch = () => {
        setSearchInput('');
        setSearchTerm('');
        setCurrentPage(1);
    };

    // Handle pagination
    const handlePageChange = (page: number) => {
        setCurrentPage(page);
    };

    const filters = {
        builderId: builderId,
        page: currentPage,
        limit,
        ...(searchTerm ? { search: searchTerm } : {})
    };

    const { data: apiData, isLoading, isError, refetch, isFetching } = useQuery({
        queryKey: ['focalPerson', filters],
        queryFn: () => focalPersonListing(filters as any),
        enabled: true,
        staleTime: 0,
        gcTime: 0,
        refetchOnWindowFocus: false,
        refetchOnMount: true,
        retry: 3
    });

    const rows = apiData?.data?.data || [];
    const totalCount = apiData?.data?.total || 0;
    const totalPages = Math.ceil(totalCount / limit);
    const customPageSize = 10;

    // Function to copy ID to clipboard
    const copyToClipboard = async (fullId: string) => {
        try {
            await navigator.clipboard.writeText(fullId);
            setCopiedId(fullId);
            // Reset the tick icon after 2 seconds
            setTimeout(() => {
                setCopiedId(null);
            }, 1000);
        } catch (err) {
            console.error('Failed to copy ID:', err);
        }
    };

    const columns = [
        {
            header: 'No',
            enableColumnFilter: false,
            cell: ({ row }: { row: any }) => {
                const index = (currentPage - 1) * customPageSize + row.index + 1;
                return <span>{index}</span>;
            },
        },
        {
            header: 'ID',
            accessorKey: 'id',
            enableColumnFilter: false,
            cell: ({ getValue }: { getValue: () => any }) => {
                const fullId = getValue();
                if (!fullId) return 'N/A';

                return (
                    <div className="d-flex align-items-center gap-2">
                        <span style={{ fontSize: '14px', fontFamily: 'monospace' }}>
                            {getTruncatedId(fullId)}
                        </span>
                        <span
                            onClick={() => copyToClipboard(fullId)}
                            style={{
                                cursor: 'pointer',
                                fontSize: '14px',
                                color: copiedId === fullId ? '#28a745' : '#6b7280',
                                transition: 'color 0.2s ease'
                            }}
                            title={copiedId === fullId ? 'Copied!' : 'Copy full ID'}
                        >
                            {copiedId === fullId ? (
                                <i className="ri-check-line"></i>
                            ) : (
                                <i className="ri-file-copy-line"></i>
                            )}
                        </span>
                    </div>
                );
            },
        },
        {
            header: 'Name',
            accessorKey: 'full_name',
            enableColumnFilter: false,
        },
        {
            header: 'Email',
            accessorKey: 'email',
            enableColumnFilter: false,
        },
        {
            header: 'Phone Number',
            accessorKey: 'phone_no',
            enableColumnFilter: false,
        },
        {
            header: 'Role Assigned',
            accessorKey: 'role',
            enableColumnFilter: false,
            cell: ({ getValue }: { getValue: () => any }) => {
                const role = getValue();
                if (!role) return 'N/A';
                return <span>{role}</span>;
            },
        },
        {
            header: 'Created At',
            accessorKey: 'createdAt',
            enableColumnFilter: false,
            cell: ({ getValue }: { getValue: () => any }) => {
                const date = getValue();
                if (!date) return 'N/A';
                const formattedDate = new Date(date).toLocaleString('en-GB', {
                    day: 'numeric',
                    month: 'short',
                    year: 'numeric',
                    hour: '2-digit',
                    minute: '2-digit',
                    hour12: true
                });
                // Format date without comma
                const [day, month, year, time, ampm] = formattedDate.split(' ');
                return `${day} ${month} ${year} ${time} ${ampm}`;
            },
        },
        {
            header: 'Updated At',
            accessorKey: 'updatedAt',
            enableColumnFilter: false,
            cell: ({ getValue }: { getValue: () => any }) => {
                const date = getValue();
                if (!date) return 'N/A';
                const formattedDate = new Date(date).toLocaleString('en-GB', {
                    day: 'numeric',
                    month: 'short',
                    year: 'numeric',
                    hour: '2-digit',
                    minute: '2-digit',
                    hour12: true
                });
                // Format date without comma
                const [day, month, year, time, ampm] = formattedDate.split(' ');
                return `${day} ${month} ${year} ${time} ${ampm}`;
            },
        },
        {
            header: 'Status',
            accessorKey: 'status',
            enableColumnFilter: false,
            cell: (cell: any) => {
                const rawVal = cell.getValue();
                let raw = '';

                if (rawVal == null) raw = '';
                else if (typeof rawVal === 'string') raw = rawVal.trim().toLowerCase();
                else if (typeof rawVal === 'object') raw = (rawVal.status ?? rawVal.value ?? rawVal.name ?? '').toString().trim().toLowerCase();
                else raw = String(rawVal).trim().toLowerCase();

                const formatted = raw ? raw.charAt(0).toUpperCase() + raw.slice(1) : 'N/A';

                switch (raw) {
                    case 'approved':
                        return (<span className="badge rounded-pill bg-success">{formatted}</span>);
                    case 'rejected':
                    case 'declined':
                        return (<span className="badge rounded-pill bg-danger">{formatted}</span>);
                    case 'pending':
                    case 'awaiting_input':
                        return (<span className="badge rounded-pill bg-warning">{formatted}</span>);
                    default:
                        return (<span className="badge rounded-pill bg-secondary">{formatted}</span>);
                }
            },
        },
        // {
        //     header: 'Action',
        //     accessorKey: 'action',
        //     enableColumnFilter: false,
        //     cell: ({row}: { row: any }) => (
        //         <UncontrolledDropdown direction="start">
        //             <DropdownToggle tag="span" style={{ cursor: 'pointer' }}>
        //                 <i className="ri-more-2-fill" style={{ fontSize: 20, color: '#6b7280' }}></i>
        //             </DropdownToggle>
        //             <DropdownMenu>
        //                 <DropdownItem
        //                     onClick={() => {
        //                         setSelectedPerson(row.original);
        //                         setModalOpen(true);
        //                     }}
        //                     className="d-flex align-items-center"
        //                 >
        //                     <i className="ri-eye-line me-2"></i> View Details
        //                 </DropdownItem>
        //             </DropdownMenu>
        //         </UncontrolledDropdown>
        //     ),
        // },
    ];

    return (
        <React.Fragment>
            <Card>
                <CardHeader className="d-flex justify-content-end align-items-center">
                    <Col md={3} className="text-end">
                        <div className="position-relative">
                            <Input
                                type="text"
                                placeholder="Search Focal Person..."
                                value={searchInput}
                                onChange={handleSearchChange}
                                className="rounded-2 py-2"
                                style={{
                                    border: '1px solid #e5e7eb',
                                    paddingRight: searchInput ? '40px' : '12px'
                                }}
                            />
                            {searchInput && (
                                <button
                                    type="button"
                                    className="btn btn-link position-absolute end-0 top-50 translate-middle-y"
                                    onClick={clearSearch}
                                    style={{
                                        border: 'none',
                                        background: 'transparent',
                                        padding: '0',
                                        width: '30px',
                                        height: '100%',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        color: '#6c757d',
                                        zIndex: 5
                                    }}
                                >
                                    <i className="ri-close-line"></i>
                                </button>
                            )}
                        </div>
                    </Col>
                </CardHeader>
                <CardBody>
                    {/* Loading State */}
                    {(isLoading || isFetching) && (
                        <div className="text-center py-5">
                            <Spinner color="primary" />
                            <p className="mt-2 text-muted">Loading...</p>
                        </div>
                    )}

                    {/* Error State */}
                    {isError && !(isLoading || isFetching) && (
                        <div className="text-center py-5">
                            <div className="text-danger mb-3">
                                <i className="ri-error-warning-line" style={{ fontSize: '3rem' }}></i>
                            </div>
                            <h5 className="text-danger">Failed to load data</h5>
                            <p className="text-muted">Please check your connection and try again.</p>
                            <button
                                className="btn btn-primary"
                                onClick={() => refetch()}
                            >
                                <i className="ri-refresh-line me-1"></i>
                                Retry
                            </button>
                        </div>
                    )}
                    {!(isLoading || isFetching) && !isError && (
                        <TableContainer
                            columns={columns}
                            data={rows}
                            isServerSide={true}
                            currentPage={currentPage}
                            totalPages={totalPages}
                            totalCount={totalCount}
                            onPageChange={handlePageChange}
                            customPageSize={limit}
                            tableClass="table-centered align-middle table-nowrap mb-0"
                            theadClass="text-muted table-light" />
                    )}
                </CardBody>
            </Card>
            {/* <FocalPersonDetailsModal
                isOpen={modalOpen}
                toggle={() => setModalOpen(false)}
                person={selectedPerson}
            /> */}
        </React.Fragment>
    );
};

export default FocalPersonTable;
